/**
 * 実行履歴の組み立て。
 *
 * 「誰が何をしたか」は2箇所に分かれて残る:
 *   ・jobs      … 機械の実行（生成・反映・承認）。状態と所要時間が付く
 *   ・audit_logs … 人の操作（素材の差し替えなど）。変更の前後が付く
 *
 * **どちらか一方だけを見ても、品目に何が起きたかは分からない。** 素材を差し替えた
 * 事実が jobs に無いため、履歴が「生成しただけ」に見えてしまう。ここで1本の
 * 時系列にまとめる。
 *
 * 表示に使う値の決定はここに集約し、画面は並べるだけにする（テストできるように）。
 */

import {
  AUDIT_ACTION_LABEL,
  JOB_STATUS_LABEL,
  JOB_TYPE_LABEL,
  type AuditLogDoc,
  type JobDoc,
  type JobStatus,
} from "./types";

/** Firestore の Timestamp と同じ形。テストから素の値を渡せるようにする */
export interface TimestampLike {
  toDate(): Date;
}

export type JobRow = JobDoc & { id: string };
export type AuditRow = AuditLogDoc & { id: string };

/** 名前を引くための最小限の利用者情報（スタッフだけが users を読める） */
export interface UserLike {
  displayName?: string | null;
  email?: string | null;
}

export interface HistoryEntry {
  id: string;
  kind: "job" | "audit";
  /** 並べ替えと表示に使う時刻。読めない場合は null（末尾へ送る） */
  at: Date | null;
  /** 操作内容 */
  action: string;
  /** 操作者 */
  actor: string;
  /** 機械の実行だけが持つ状態。人の操作は完了済みなので null */
  status: JobStatus | null;
  statusLabel: string | null;
  job?: JobRow;
  audit?: AuditRow;
}

function toDate(v: TimestampLike | null | undefined): Date | null {
  return v && typeof v.toDate === "function" ? v.toDate() : null;
}

/**
 * 操作者の表示。
 *
 * 焼き込んだ表示名 → メール → 名簿の引き直し、の順で見る。焼き込みを先に見るのは、
 * 改名・退職の後でも**当時の名前**で読めるようにするため。
 * どれも取れないときは uid の頭を出す（「不明」だけだと追跡の手掛かりが消える）。
 */
export function actorLabel(
  uid: string | null | undefined,
  name: string | null | undefined,
  email: string | null | undefined,
  users: Record<string, UserLike>,
): string {
  if (name) return name;
  if (email) return email;
  if (!uid) return "—";
  const u = users[uid];
  if (u?.displayName) return u.displayName;
  if (u?.email) return u.email;
  return `不明（uid ${uid.slice(0, 8)}）`;
}

/** ジョブ1件を履歴の行にする */
export function jobEntry(job: JobRow, users: Record<string, UserLike>): HistoryEntry {
  return {
    id: `job:${job.id}`,
    kind: "job",
    // createdAt が正。古いジョブには無いので開始・終了で補う
    at: toDate(job.createdAt) ?? toDate(job.startedAt) ?? toDate(job.finishedAt),
    action: JOB_TYPE_LABEL[job.type] ?? job.type,
    actor: actorLabel(job.requestedBy, job.requestedByName, job.requestedByEmail, users),
    status: job.status,
    statusLabel: JOB_STATUS_LABEL[job.status] ?? job.status,
    job,
  };
}

/** 台帳1件を履歴の行にする */
export function auditEntry(log: AuditRow, users: Record<string, UserLike>): HistoryEntry {
  return {
    id: `audit:${log.id}`,
    kind: "audit",
    at: toDate(log.createdAt),
    action: AUDIT_ACTION_LABEL[log.action] ?? log.action,
    actor: actorLabel(log.actorUid, log.actorName, log.actorEmail, users),
    status: null,
    statusLabel: null,
    audit: log,
  };
}

/** 新しい順に1本の時系列へまとめる。時刻の読めない行は末尾へ送る */
export function buildHistory(
  jobs: JobRow[],
  audits: AuditRow[],
  users: Record<string, UserLike> = {},
): HistoryEntry[] {
  const rows = [
    ...jobs.map((j) => jobEntry(j, users)),
    ...audits.map((a) => auditEntry(a, users)),
  ];
  return rows.sort((a, b) => {
    if (a.at && b.at) return b.at.getTime() - a.at.getTime();
    if (a.at) return -1;
    if (b.at) return 1;
    return a.id.localeCompare(b.id);
  });
}

/**
 * 出ているマニュアルが、今の素材から作られたものか。
 *
 * 素材を差し替えた後の analyze が終わっていなければ、画面に出ているマニュアルは
 * **古い素材のもの**になる。黙って出すと、差し替えたのに何も変わらないように見え、
 * さらに⑧の採点の基準まで古いままになる。
 */
export function manualIsStale(
  materialsUpdatedAt: TimestampLike | null | undefined,
  jobs: JobRow[],
): boolean {
  const changed = toDate(materialsUpdatedAt);
  if (!changed) return false;
  const lastAnalyze = jobs
    .filter((j) => j.type === "analyze" && j.status === "succeeded")
    .map((j) => toDate(j.finishedAt))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0];
  // 一度も生成していないなら「古い」ではない（まだ出来ていないだけ）
  return lastAnalyze ? changed.getTime() > lastAnalyze.getTime() : false;
}
